import React from 'react';
import { useMutation, useQueryClient, MutationOptions } from '@tanstack/react-query';
import { deleteListEntry } from 'Services';
import { RQ_SINGLE_LIST_ENTRY, RQ_LIST_ENTRIES, RQ_LIST_BY_ID, callAll } from 'Utils';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useTranslation } from 'react-i18next';
import { getTranslationErrorHandling } from '../helper';

type HookArgs = {
    listEntryId: number;
    listId: number;
    listName: string;
    mutationOptions?: Omit<MutationOptions<any>, 'mutationKey'>;
    rowData: RangeList;
};

function useDeleteSingleListEntry({ rowData, listEntryId, listId, listName, mutationOptions }: HookArgs) {
    const queryClient = useQueryClient();
    const { addAlert } = useAlertBanner();
    const { t } = useTranslation();
    const { addTechnicalErrorModal } = useReduxModal();
    const singleListEntryKey = getTranslationErrorHandling(t, 'singleListEntry');
    const errorStringKey = getTranslationErrorHandling(t);

    const errorHandler = React.useCallback(
        (error: any) => {
            const handleNotFound = () => {
                addTechnicalErrorModal('', singleListEntryKey('Entry Deleted'), () => {}, {
                    status: 'warning',
                    content: singleListEntryKey('listEntryDeleted')
                });
            };

            const handleDefault = () => {
                const preContentMessage = errorStringKey('entryFailed');
                addTechnicalErrorModal(preContentMessage, errorStringKey('unableToRemoveEntries'));
            };

            switch (error?.status) {
                case 404:
                    handleNotFound();
                    break;
                default:
                    handleDefault();
                    break;
            }
        },
        [addTechnicalErrorModal, errorStringKey, singleListEntryKey]
    );

    const defaultSuccess = () => {
        const content = t('delete single list entry success msg', { listName });
        addAlert({
            id: 'delete-single-list-entry-success',
            content,
            status: 'success'
        });
    };
    return useMutation(
        [RQ_SINGLE_LIST_ENTRY, listEntryId, listId],
        () => deleteListEntry(listEntryId, listId, { ...rowData }),
        {
            ...mutationOptions,
            onSuccess: callAll(defaultSuccess, mutationOptions?.onSuccess),
            onError: callAll(errorHandler, mutationOptions?.onError),
            onSettled: () => {
                queryClient.invalidateQueries([RQ_LIST_BY_ID, listId]);
                queryClient.invalidateQueries([RQ_LIST_ENTRIES]);
            }
        }
    );
}

export default useDeleteSingleListEntry;
